import {useCheckoutTrackingUrl} from '@aboutyou/config';
import {Log} from '@aboutyou/mlm';
import {useCustomerBiData, useUser} from '@aboutyou/user';

export type Device = 'mobile-web' | 'desktop-web' | 'ios-app' | 'android-app';

interface COTrackingEvent {
  shopId: number;
  device: 'mobile-web' | 'desktop-web' | 'ios-app' | 'android-app';
}

interface ComponentLoadEvent extends COTrackingEvent {
  type: 'ComponentLoadEvent';
  customerId: number | null;
}

interface IntentConfirmationEvent extends COTrackingEvent {
  type: 'IntentConfirmationEvent';
  customerId: number | null;
  orderId: number;
}

interface OSPEvent extends COTrackingEvent {
  type: 'OSPEvent';
  cbd: string;
  paymentType: string;
  orderDate: string;
}

interface CheckoutErrorEvent extends COTrackingEvent {
  type: 'CheckoutError';
  customerId: number | null;
  message?: string;
  endpoint?: string;
  statusCode?: string;
}

interface CheckoutLoadTiming extends COTrackingEvent {
  type: 'CheckoutLoadTiming';
  customerId: number | null;
  duration: number;
  startTrigger:
    | 'checkout-page-entry'
    | 'web-component-render-start'
    | 'go-to-checkout-click'
    | 'before-app-go-to-checkout-click';
  endTrigger: 'checkout-load';
}

interface ComponentPreloadErrorEvent extends COTrackingEvent {
  type: 'ComponentPreloadError';
  customerId?: number | null;
  message: string;
}

type Event =
  | ComponentLoadEvent
  | IntentConfirmationEvent
  | OSPEvent
  | CheckoutErrorEvent
  | CheckoutLoadTiming
  | ComponentPreloadErrorEvent;

export const useCOTracking = () => {
  const checkoutTrackingUrl = useCheckoutTrackingUrl();
  const biData = useCustomerBiData();
  const {data: user} = useUser();

  function getEndpointForEvent(event: Event) {
    if (event.type === 'ComponentPreloadError') {
      return checkoutTrackingUrl + '/preload-checkout-error';
    }
    if (event.type === 'ComponentLoadEvent') {
      return checkoutTrackingUrl + '/checkout-load';
    }
    if (event.type === 'IntentConfirmationEvent') {
      return checkoutTrackingUrl + '/intent-confirmation';
    }
    if (event.type === 'CheckoutError') {
      return checkoutTrackingUrl + '/checkout-error';
    }
    if (event.type === 'OSPEvent') {
      return checkoutTrackingUrl + '/order-success-page';
    }
    if (event.type === 'CheckoutLoadTiming') {
      return checkoutTrackingUrl + '/load-timing';
    }
  }

  async function track(event: Event) {
    const endpoint = getEndpointForEvent(event);

    if (!endpoint) {
      Log.warn('@aboutyou/tracking', 'CO Tracking event not supported', {
        event,
      });
      return;
    }

    try {
      const {type, ...body} = event;
      const res = await fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          ...body,
          // on app we dont get biData and default value is 0
          // we want mark it a bit different so that we know differentiate between default and actual value
          pc180Score: user && user.checkoutSession ? biData.pc180Score : -1,
        }),
        priority: 'low',
      } as RequestInit);
      if (process.env.NODE_ENV !== 'production') {
        Log.info(
          '@aboutyou/tracking',
          `COT event fired successfully for ${type}`,
          {
            event,
            res,
          },
        );
      }
    } catch (error) {
      Log.error(
        '@aboutyou/tracking',
        'Something went wrong calling COT endpoint',
        {error},
      );
    }
  }

  function getDevice(appDevice: string | null): Device {
    if (appDevice === 'ios') {
      return 'ios-app';
    }
    if (appDevice === 'android') {
      return 'android-app';
    }
    if (process.env.STACK === 'mobile') {
      return 'mobile-web';
    }
    if (process.env.STACK === 'desktop') {
      return 'desktop-web';
    }

    return 'desktop-web';
  }

  return {
    track,
    getDevice,
    targetUrl: checkoutTrackingUrl,
  };
};
